import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Box, Typography, Tooltip } from '@mui/material';
import { stageColors } from '../../theme';
import { getStageDisplayName } from '../../utils/helpers';

const stages = [
  'new',
  'warming',
  'dm_sent',
  'replied',
  'call_scheduled',
  'call_completed',
  'proposal_sent',
  'negotiating',
  'won',
];

const PipelineCard = ({ data }) => {
  const navigate = useNavigate();

  // Calculate total for percentage
  const total = stages.reduce((sum, stage) => sum + (data?.[stage] || 0), 0);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Pipeline Overview
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {total} total leads
          </Typography>
        </Box>

        {/* Visual Pipeline Bar */}
        <Box sx={{ display: 'flex', height: 16, borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
          {stages.map((stage) => {
            const count = data?.[stage] || 0;
            const percentage = total > 0 ? (count / total) * 100 : 0;
            if (percentage === 0) return null;

            return (
              <Tooltip
                key={stage}
                title={`${getStageDisplayName(stage)}: ${count}`}
                arrow
              >
                <Box
                  sx={{
                    width: `${percentage}%`,
                    bgcolor: stageColors[stage],
                    cursor: 'pointer',
                    transition: 'opacity 0.2s',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => navigate(`/leads?stage=${stage}`)}
                />
              </Tooltip>
            );
          })}
        </Box>

        {/* Stage List */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
          {stages.map((stage) => {
            const count = data?.[stage] || 0;

            return (
              <Box
                key={stage}
                onClick={() => navigate(`/leads?stage=${stage}`)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  py: 0.5,
                  px: 0.75,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.50' },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: stageColors[stage],
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '0.7rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {getStageDisplayName(stage)}
                  </Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.8rem', lineHeight: 1 }}>
                    {count}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PipelineCard;
